import joi from 'joi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BURNABLE_REWARD_TYPES,
  RewardAttributes,
  isSoldOut,
} from '@loyalty/shared-common/rewards-api-client';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { useProgram } from '../../../loyalty/ProgramContext';
import { useRewards } from '../../../rewards/RewardsContext';

const offerRewardFormSchema = joi.object({
  rewardId: joi.string(),
  comment: joi.string(),
});

export interface OfferRewardDialogProps {
  profile: ProfileAttributes;
  onClose: () => void;
  onOffer?: () => Promise<void> | void;
}

export function OfferRewardDialog({ profile, onClose, onOffer }: OfferRewardDialogProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { notify } = useAlerts();
  const { program } = useProgram();
  const { refreshReward } = useRewards();

  const [submitting, setSubmitting] = useState(false);
  const [rewards, setRewards] = useState<RewardAttributes[]>([]);

  const { rewardsApi, eventProcessingApi } = useApi();

  const resolver = useJoiResolver(offerRewardFormSchema);
  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver,
    defaultValues: {
      rewardId: '',
      comment: '',
    },
  });

  useEffect(() => {
    rewardsApi
      .getRewards(program.id)
      .then(rewards => {
        setRewards(
          rewards.filter(
            reward =>
              BURNABLE_REWARD_TYPES.includes(reward.type) &&
              !reward.deletedAt &&
              !isSoldOut(reward),
          ),
        );
      })
      .catch(console.error);
  }, [program.id, rewardsApi]);

  const rewardsOptions = useMemo(
    () =>
      rewards.map(reward => ({
        value: reward.id,
        text: getLocalizedProperty(reward, 'name', language),
      })),
    [rewards, language],
  );

  useEffect(() => {
    const selected = getValues('rewardId');
    if (rewards.length > 0 && (!selected || rewards.every(({ id }) => id !== selected))) {
      setValue('rewardId', rewards[0].id);
    }
  }, [rewards, getValues, setValue]);

  const handleOffer = useCallback(async () => {
    setSubmitting(true);
    try {
      const { rewardId, comment } = getValues();
      await eventProcessingApi.offerReward(program.id, profile.id, {
        rewardId,
        i18nAttributes: Object.fromEntries(
          program.languages.map(language => [language, { description: `<p>${comment}</p>` }]),
        ),
      });
      notify(
        t('profiles.details.rewards.offer-dialog.offer.success', {
          member: profile.firstName || t('profile.this-member'),
        }),
        'success',
      );
      await refreshReward(rewardId);
      await onOffer?.();
      onClose();
    } catch (error) {
      console.error(error);
      notify(t('profiles.details.rewards.offer-dialog.offer.error', { error }), 'error');
    } finally {
      setSubmitting(false);
    }
  }, [program, profile, eventProcessingApi, notify, refreshReward, onOffer, onClose, t, getValues]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <IconButton
        sx={{
          fontSize: 16,
          padding: 1.5,
          position: 'absolute',
          right: 0,
          top: 0,
        }}
        onClick={onClose}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogTitle>{t('profiles.details.rewards.offer-dialog.title')}</DialogTitle>
      <form onSubmit={handleSubmit(handleOffer, console.error)}>
        <DialogContent sx={{ textAlign: 'start !important' }}>
          <Controller
            control={control}
            name="rewardId"
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                inputRef={ref}
                select
                placeholder={
                  rewardsOptions?.length === 0
                    ? t('profiles.details.rewards.offer-dialog.no-reward.placeholder')
                    : t('profiles.details.rewards.offer-dialog.reward.placeholder')
                }
              >
                {rewardsOptions.map(val => (
                  <MenuItem key={val.value} value={val.value}>
                    {val.text}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Box sx={{ marginTop: 1, marginBottom: 1 }}>
            {t('profiles.details.rewards.offer-dialog.comment.label')}
          </Box>
          <Controller
            control={control}
            name="comment"
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                inputRef={ref}
                multiline
                fullWidth
                rows={3}
                placeholder={t('profiles.details.rewards.offer-dialog.comment.placeholder', {
                  member: profile.firstName || t('profile.this-member'),
                })}
              />
            )}
          />
        </DialogContent>

        <DialogActions>
          <Button color="grey" onClick={onClose}>
            {t('generic.actions.cancel')}
          </Button>
          <Button
            color="success"
            type="submit"
            startIcon={submitting && <FontAwesomeIcon icon={faSpinner} spin />}
            disabled={submitting}
            autoFocus
          >
            {t('profiles.details.rewards.offer-dialog.offer.label')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
